import { colorFillAlter, colorText, ConfigProvider, Flex, Input, Select, Space, Switch, Tooltip, Typography } from '@arkestro/arkestro-design-system';
import { Controller, useFormContext } from 'react-hook-form';
import useTranslation from 'next-translate/useTranslation';
import { ReactNode, useContext, useMemo } from 'react';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { UserContext } from '@components/providers/UserProvider/UserProvider';
import RichTextEditor from '@components/shared/RichTextEditor/RichTextEditor';
import { TagSelect } from '@shared/Tag/TagSelect/TagSelect';
import { CustomFieldsPanel } from '@components/RfxDrawers/shared/CustomFieldsPanel/CustomFieldsPanel';
import styles from './OverviewPanel.module.scss';

interface Props {
  businessUnitField?: ReactNode;
  canManageTags?: boolean;
  companyTags?: { label: string; value: string }[];
  disableFields: boolean;
  disableTemplateToggle?: boolean;
  isEditing?: boolean;
  showCustomFields?: boolean;
  showTags?: boolean;
  showTemplateToggle?: boolean;
  spendCategories: { label: string; value: number }[];
}

const RTE_MAX_HEIGHT = 1000;

interface BusinessUnitFieldProps {
  disabled: boolean;
  options: { label: string; value: number }[];
  override?: ReactNode;
}

/** The business unit picker, unless the drawer passes its own field in its place. */
const BusinessUnitField = ({ disabled, options, override }: BusinessUnitFieldProps) => {
  const { t } = useTranslation('common');

  if (override != null) return override;

  return (
    <Flex gap={4} vertical>
      <Typography className={styles.text}>{t('rfx_drawer.business_unit_label')}</Typography>
      <Controller
        name="businessUnitId"
        render={({ field: { value, onChange } }) => (
          <Select
            allowClear
            className={styles.select}
            dataTestId='business-unit-select'
            disabled={disabled}
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            onChange={onChange}
            options={options}
            placeholder={t('rfx_drawer.select_one_placeholder')}
            value={value}
          />
        )}
      />
    </Flex>
  );
};

export const OverviewPanel = ({
  businessUnitField,
  canManageTags = false,
  companyTags = [],
  disableFields,
  disableTemplateToggle = false,
  isEditing = false,
  showCustomFields = false,
  showTags = false,
  showTemplateToggle = true,
  spendCategories
}: Props) => {
  const user = useContext(UserContext);
  const { t } = useTranslation('common');
  const { watch } = useFormContext();

  const CONTRACT_LENGTH_OPTIONS = useMemo(() => [
    {
      label: t('rfx_drawer.months'),
      value: 'months',
    },
    {
      label: t('rfx_drawer.years'),
      value: 'years',
    },
  ], [t]);

  const QUOTE_TYPES = useMemo(() => [
    {
      label: t('rfx_drawer.quote_types.one_time'),
      value: 'one_time',
    },
    {
      label: t('rfx_drawer.quote_types.price_agreement'),
      value: 'price_agreement',
    },
  ], [t]);

  const businessUnits = () => {
    if (!user.business_units) return [];

    return user.business_units ? user.business_units.map((bu: { id: number; name: string }) => ({
      label: bu.name,
      value: bu.id
    })) : [];
  };

  const watchQuoteType = watch('quoteType');
  const isPriceAgreement = watchQuoteType === 'price_agreement';

  let note = t('rfx_drawer.note');
  let descriptionLabel = t('rfx_drawer.event_description_label');

  if (isEditing) {
    note = t('rfx_drawer.note_edit');
    descriptionLabel = t('rfx_drawer.event_description_label_edit');
  }

  const templateTooltip = disableTemplateToggle
    ? t('rfx_drawer.template_tooltip_disabled')
    : t('rfx_drawer.template_tooltip');

  return (
    <Flex vertical>
      <Flex className={styles.panel} data-testid='recurring-event-overview' gap={20} vertical>
        <Flex justify='space-between'>
          <Typography className={styles.overviewHeader}>{t('rfx_drawer.overview')}</Typography>
          <Flex gap={24}>
            {showTemplateToggle && <Flex gap={16}>
              <Flex align='center' gap={4}>
                <Typography className={styles.text}>{t('rfx_drawer.is_template')}</Typography>
                <Tooltip placement='bottom' title={templateTooltip}>
                  <QuestionCircleOutlined className={styles.tooltipIcon} data-testid='template-tooltip-icon' />
                </Tooltip>
              </Flex>
              <Controller
                name="isTemplate"
                render={({ field: { value, onChange } }) => (
                  <Switch
                    checked={value}
                    dataTestId='template-switch'
                    disabled={disableFields || disableTemplateToggle}
                    onChange={onChange}
                  />
                )}
              />
            </Flex>}
            <Flex gap={16}>
              <Flex align='center' gap={4}>
                <Typography className={styles.text}>{t('rfx_drawer.is_test')}</Typography>
                <Tooltip placement='bottom' title={t('rfx_drawer.test_flag_tooltip')}>
                  <QuestionCircleOutlined className={styles.tooltipIcon} />
                </Tooltip>
              </Flex>
              <Controller
                name="isTest"
                render={({ field: { value, onChange } }) => (
                  <Switch
                    checked={value}
                    dataTestId='test-switch'
                    disabled={disableFields}
                    onChange={onChange}
                  />
                )}
              />
            </Flex>
          </Flex>
        </Flex>
        {/* Event Title */}
        <Flex gap={4} vertical>
          <Flex align='center' gap={4}>
            <span className={styles.required}>*</span>
            <Typography className={styles.text}>{t('rfx_drawer.event_title_label')}</Typography>
          </Flex>
          <Controller
            name="eventTitle"
            render={({ field: { value, onChange } }) => (
              <Input
                data-testid='event-title-input'
                disabled={disableFields}
                onChange={onChange}
                placeholder={t('rfx_drawer.event_title_placeholder')}
                value={value}
              />
            )}
            rules={{ required: true }}
          />
        </Flex>

        {/* Event Description */}
        <Flex gap={4} vertical>
          <Typography className={styles.text}>{descriptionLabel}</Typography>
          <Controller
            name="eventDescription"
            render={({ field: { value, onChange } }) => (
              <RichTextEditor
                disabled={disableFields}
                maxHeight={RTE_MAX_HEIGHT}
                onChange={onChange}
                placeholder={t('rfx_drawer.event_description_placeholder')}
                resize
                statusbar
                value={value}
              />
            )}
          />
        </Flex>

        {/* Business Unit Selection */}
        <BusinessUnitField disabled={disableFields} options={businessUnits()} override={businessUnitField} />

        {/* Tags */}
        {showTags && (
          <Tooltip
            placement='bottom'
            title={canManageTags ? undefined : t('rfx_drawer.tags_no_permission_tooltip')}
          >
            <Flex gap={4} vertical>
              <Flex align='center' gap={4}>
                <Typography className={styles.text}>{t('general.tags')}</Typography>
                {canManageTags && (
                  <Tooltip placement='bottom' title={t('rfx_drawer.tags_tooltip')}>
                    <QuestionCircleOutlined className={styles.tooltipIcon} data-testid='tags-info-icon' />
                  </Tooltip>
                )}
              </Flex>
              <Controller
                name="tagNames"
                render={({ field: { value, onChange } }) => (
                  <TagSelect
                    className={styles.select}
                    disabled={disableFields || !canManageTags}
                    onChange={onChange}
                    options={companyTags}
                    value={value}
                  />
                )}
              />
            </Flex>
          </Tooltip>
        )}

        {showCustomFields && showTags && (
          <CustomFieldsPanel disableFields={disableFields} />
        )}

        {/* Spend Type and Quote Type */}
        {!showTags && (<Flex gap={16}>
          <Flex className={styles.recurrenceFlex} gap={4} vertical>
            <Typography className={styles.text}>{t('rfx_drawer.spend_category_label')}</Typography>
            <Controller
              name="spendTypeId"
              render={({ field: { value, onChange } }) => (
                <Select
                  allowClear
                  className={styles.select}
                  dataTestId='spend-type-select'
                  disabled={disableFields}
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  onChange={onChange}
                  options={spendCategories}
                  placeholder={t('rfx_drawer.select_one_placeholder')}
                  value={value}
                />
              )}
            />
          </Flex>

          <Flex className={styles.recurrenceFlex} gap={4} vertical>
            <Typography className={styles.text}>{t('rfx_drawer.contract_types_label')}</Typography>
            <Controller
              name="quoteType"
              render={({ field: { value, onChange } }) => (
                <Select
                  allowClear
                  className={styles.select}
                  dataTestId='quote-type-select'
                  disabled={disableFields}
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  onChange={onChange}
                  options={QUOTE_TYPES}
                  placeholder={t('rfx_drawer.select_one_placeholder')}
                  value={value}
                />
              )}
            />
          </Flex>
        </Flex>)}

        {!showTags && isPriceAgreement && <Flex gap={4} vertical>
          <Typography className={styles.text}>{t('rfx_drawer.contract_length_label')}</Typography>
          <Space.Compact>
            <Controller
              name="contractLength"
              render={({ field }) => (
                <Input
                  placeholder={t('rfx_drawer.numeric_value')}
                  type='number'
                  {...field}
                  data-testid='contract-length-number-input'
                />
              )}
              rules={
                { min: 1 }
              }
            />
            <Controller
              name="contractLengthUnit"
              render={({ field: { value, onChange } }) => (
                <ConfigProvider
                  theme={{
                    components: {
                      Select: {
                        colorBgContainer: colorFillAlter,
                        colorText,
                      },
                    },
                  }}
                >
                  <Select data-testid='contract-length-unit-select' onChange={onChange} options={CONTRACT_LENGTH_OPTIONS} value={value} />
                </ConfigProvider>
              )}
            />
          </Space.Compact>
        </Flex>}

        <Typography className={styles.note}>{note}</Typography>
      </Flex>
    </Flex>
  );
};
